//- ANGULAR
import { Component, Input, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

//- TECH
import { TechMessageModule } from '@tech/components/tech-message';
import { TechIconModule } from '@tech/components/tech-icon';
import { TechLoaderModule } from '@tech/components/tech-loader';
import { TechButtonModule } from '@tech/components/tech-button';
import { TechTooltipModule } from '@tech/components/tech-tooltip';
import { TechCardModule } from '@tech/components/tech-card';
import { TechScrollbarModule } from '@tech/components/tech-scrollbar';
import {
  sendAppNotification,
  sendAppToast,
} from '@tech/intranet-core-protocol';

//- OWN
import {
  NotificationMessageData,
  NotificationhubService,
} from '@app/shared/services/notificationhub/notificationhub.service';
import { NotificationService } from '@app/shared/services/notification/notification.service';
import { FileProcessorService } from '@app/shared/services/wasm/file-processor/file-processor.service';
import { IFilters } from '@features/receipts/components/permanent-table-paginated/permanent-table-paginated.component';
import { environment } from '@environments/environment';
import { NavbarComponent } from '../navbar/navbar.component';
import { InputFileComponent } from '../input-file/input-file.component';
import { OperationResultCheckComponent } from '../operation-result-check/operation-result-check.component';
import { StageTableComponent } from '../stage-table/stage-table.component';
import { FileParameter, UploadFilesResponse } from '@tech/api-clients/receipts';
import { PrettyConsole } from '@app/shared/utils';
import { Severity } from '@tech/api-clients/notification';
import { Store } from '@ngrx/store';

export type ReceiptDisplay = {
  file: string;
  ok: boolean;
  [key: string]: string | number | boolean | Date | undefined;
};

@Component({
  selector: 'app-receipt',
  standalone: true,
  imports: [
    TechMessageModule,
    TechIconModule,
    TechCardModule,
    TechLoaderModule,
    TechButtonModule,
    TechTooltipModule,
    TechScrollbarModule,
    NavbarComponent,
    InputFileComponent,
    OperationResultCheckComponent,
    StageTableComponent,
    TranslateModule,
  ],
  templateUrl: './receipt.component.html',
  styleUrl: './receipt.component.scss',
})
export class ReceiptComponent {
  // displaying
  @Input() staged_receipts: ReceiptDisplay[] = [];
  @Input() display_columns: string[] = [];
  @Input() available_filters: IFilters[] = [];
  hiddenColumns: string[] = [];

  // staging
  something_uploaded: boolean = false;
  staged_file_count: number = 0;
  stage_errors: boolean = false;
  loading: boolean = false;
  uploading: boolean = false;
  staged_files: Set<File> = new Set();
  private sse_connected: boolean = false;

  // HACK: WASM
  @Input() validation_schema: {} = {};
  @Input() wasm_split_pattern: string = ';';
  // From Childs
  @Input() sse_name: string = '';
  @Input() upload_field_text: string = '';
  @Input() upload_handler: Function = function () {};
  @Input() callme: Function = function () {};
  @Input() lookupPermanent: Function = function () {};
  @Input() get_paginated_data: Function = function () {};

  protected store = inject(Store);
  protected translate = inject(TranslateService);
  constructor(
    protected dialog: MatDialog,
    protected fileProcessor: FileProcessorService,
    protected notify: NotificationService,
    protected hub: NotificationhubService
  ) {
    this._connectSSE();
    this.handleSuccess.bind(this);
    this.handleError.bind(this);
  }

  /**
   * @brief Sends a toast through the Angular Store
   */
  protected core_toast = (msg: string, severity: Severity) => {
    this.store.dispatch(sendAppToast({ body: msg, severity }));
  };

  /**
   * @brief Sends a notification through the Angular Store
   */
  protected core_notify = (
    subject: string,
    msg: string,
    s: Severity,
    timeout: number = 5000
  ) => {
	let translated_message = msg.replace(/@@(.*?)@@/g, (_, key) => {
	  return this.translate.instant(key.trim());
	});
    this.store.dispatch(
      sendAppNotification({
        subject: subject,
        body: translated_message,
        severity: s,
        timeout: timeout,
      })
    );
  };

  /**
   * @brief ensure that the sse connection is stablished
   * If the connection is not stablished it sets a timeout
   * of 5 seconds to try again
   */
  protected async _connectSSE() {
    const next = (val: NotificationMessageData) => {
      switch (val.type) {
        case 'success':
          this.core_notify(this.sse_name, val.msg, Severity.Success);
          break;
        case 'error':
          this.core_notify(this.sse_name, val.msg, Severity.Danger);
          break;
        case 'warning':
          this.core_notify(this.sse_name, val.msg, Severity.Warning);
          break;
        case 'info':
          this.core_notify(this.sse_name, val.msg, Severity.Info);
          break;
      }
      this.sse_connected = true;
    };

    const err = (e: any) => {
      console.log(e);
      if (!this.sse_connected) {
      }
    };
    // BUG: something happens with this
    if (this.sse_connected) {
      return;
    } else {
      setTimeout(
        () =>
          this.hub
            .connectSSE(this.sse_name, environment.apiUrl)
            .subscribe({ next, error: err }),
        5000
      );
    }
  }

  protected handleSuccess = (res: UploadFilesResponse) => {
    this.uploading = false;
    if (res.success) {
      this.core_notify(this.sse_name, res.message ?? '', Severity.Success);
      PrettyConsole.SUCCESS(`Succeded: ${res.message}`);
    } else {
      this.core_notify(this.sse_name, res.message ?? '', Severity.Warning);
      PrettyConsole.WARNING(`WARN: ${res.message}`);
    }
    this.cancelUpload();
  };

  protected handleError = (err: any) => {
    this.uploading = false;

    this.core_notify(this.sse_name, err ?? '', Severity.Danger);
    PrettyConsole.ERROR(`Upload error ${err}`);
    this.cancelUpload();
  };

  /**
   * @brief This function does some tricks to allow us to report and handle this from this parent function
   ** |-------------------------------------------------------|
   ** |   parent------------|          sends app Notification |
   ** |-----^---------------|---------------------^-----------|
   **       |               |                     |
   **       |               |                     |
   ** instantiates  calls and subscribes  sends next or error
   **       |               |                     |
   ** |-----|---------------v---------------------|-----------|
   ** |   child       backend client -------------|           |
   ** |-------------------------------------------------------|
   *
   */
  handleUpload(_: Event) {
    if (this.staged_files.size === 0) {
      this.core_toast('No receipts staged for upload.', Severity.Info);
      return;
    }
    if (this.staged_receipts.some((d) => d.ok === false)) {
      this.core_notify(
        'handleUpload',
        `Can't upload invalid receipts, correct them and reupload.`,
        Severity.Danger,
        5000
      );
      return;
    }
    const file_params: FileParameter[] = [];
    this.staged_files.forEach((f) =>
      file_params.push({ data: f, fileName: f.name })
    );

    this.uploading = true;
    this.upload_handler(file_params).subscribe({
      next: this.handleSuccess,
      error: this.handleError,
    });
  }

  /**
   * @brief The files loaded from any of the receipts will come here
   * @description We need to know what is the type of the DTO
   * for the application to show it (if any errors are encountered)
   */
  async onFilesLoaded(files: File[]) {
    let processing_list = files.length;
    this.something_uploaded = true;
    this.loading = true;
    this.staged_file_count += files.length;
    const ERRORS_PER_FILE = 20;
    console.log(this.validation_schema);
    files.map((file) => {
      let result_promise = this.fileProcessor.processFile(
        file,
        this.wasm_split_pattern,
        this.validation_schema,
        ERRORS_PER_FILE
      );

      result_promise.then(
        (resolve) => {
          console.log(resolve);
          PrettyConsole.INFO(`Promise fullfilled for ${file.name}`);
          let x: any[] = resolve;
          if (x.length !== 0) {
            this.core_notify(
              'OnFilesLoaded',
              `${file.name}\nValidation failed.\nCheck table`,
              Severity.Danger
            );
          }
          x.map((n) => {
            let res = { file: file.name, ok: false, ...n };
            this.staged_receipts.push(res);
          });
          if (this.staged_receipts.length !== 0) {
            this.stage_errors = true;
          }
          processing_list--;
          if (processing_list === 0) {
            this.loading = false;
          }
          this.staged_files.add(file);
          if (this.stage_errors === false) {
            this.core_notify(
              'OnFilesLoaded',
              `${file.name} added for upload`,
              Severity.Info,
              3000
            );
          }
        },
        (_reject) => {
          this.core_notify(
            'warning',
            `${file.name}\nPromise rejected.\nYou should no see this unless something really bad happened`,
            Severity.Warning
          );
          console.log(_reject);
          processing_list--;
          if (processing_list === 0) {
            this.loading = false;
          }
        }
      );
    });
  }

  cancelUpload() {
    this.staged_receipts = [];
    this.staged_file_count = 0;
    this.stage_errors = false;
    this.loading = false;
    this.something_uploaded = false;
    this.staged_files.clear();
  }
}
